import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IoIosSearch } from "react-icons/io";
import Input from "../Input/Input";
import styles from "./SearchEventsInput.module.scss";
import { useConnection } from "../../hooks/useConnection";
import buildImageUrl from "../../utils/buildImageUrl";

const INITIAL_UI_STATE = {
  results: [],
  open: false,
  activeIndex: -1,
  loading: false,
};

const SearchResultItem = memo(function SearchResultItem({
  item,
  index,
  isActive,
  onHover,
  onSelect,
}) {
  const handleMouseEnter = useCallback(() => {
    onHover(index);
  }, [index, onHover]);

  const handleMouseDown = useCallback(() => {
    onSelect(item);
  }, [item, onSelect]);

  return (
    <li
      className={`${styles.resultItem} ${isActive ? styles.active : ""}`}
      role="option"
      aria-selected={isActive}
      onMouseEnter={handleMouseEnter}
      onMouseDown={handleMouseDown}
      tabIndex={-1}
    >
      <img
        src={item.image}
        alt={item.name}
        className={styles.thumb}
        loading="lazy"
      />

      <div className={styles.meta}>
        <span className={styles.title}>{item.event_name}</span>
        <span className={styles.subtitle}>{item.location || "—"}</span>
      </div>
    </li>
  );
});

const SearchEventsInput = memo(function SearchEventsInput({
  onSelect,
  floating = false,
  inputContainer = "",
  minLength = 2,
  debounceMs = 500,
  limit = 8,
}) {
  const [search, setSearch] = useState("");
  const [uiState, setUiState] = useState(INITIAL_UI_STATE);

  const { results, open, activeIndex, loading } = uiState;

  const boxRef = useRef(null);
  const requestIdRef = useRef(0);
  const skipNextSearchRef = useRef(false);
  const cacheRef = useRef(new Map());

  const connection = useConnection();
  const connectionRef = useRef(connection);

  useEffect(() => {
    connectionRef.current = connection;
  }, [connection]);

  const searchText = search.trim();
  const canSearch = searchText.length >= minLength;

  const inputIcon = useMemo(() => <IoIosSearch />, []);

  const inputStyle = useMemo(() => ({ height: "40px" }), []);

  const wrapperClassName = useMemo(() => {
    return [
      styles.searchWrap,
      styles.inputContainer,
      floating ? styles.floating : "",
      inputContainer,
    ]
      .filter(Boolean)
      .join(" ");
  }, [floating, inputContainer]);

  const mapEvents = useCallback(
    (items = []) => {
      return items.slice(0, limit).map((item) => ({
        event_id: item.event_id,
        artist_id: item.artist_id,
        is_favorite: item.is_favorite,
        event_name: item.event_name,
        name: item.artist_name || item.event_name || "Evento",
        location: item.venue_name || "",
        image: buildImageUrl(item.event_image || item.artist_image),
        event_dates_list: item.event_dates_list,
      }));
    },
    [limit]
  );

  const resetResults = useCallback(() => {
    setUiState((prev) => {
      if (
        prev.results.length === 0 &&
        !prev.loading &&
        prev.activeIndex === -1
      ) {
        return prev;
      }

      return {
        ...prev,
        results: [],
        loading: false,
        activeIndex: -1,
      };
    });
  }, []);

  const searchEventsRequest = useCallback(
    async (text) => {
      const normalizedText = text.trim();

      if (!normalizedText) {
        resetResults();
        return;
      }

      const cachedResults = cacheRef.current.get(normalizedText);

      if (cachedResults) {
        setUiState((prev) => ({
          ...prev,
          results: cachedResults,
          activeIndex: cachedResults.length ? 0 : -1,
          loading: false,
          open: true,
        }));

        return;
      }

      const currentRequestId = ++requestIdRef.current;

      setUiState((prev) => ({
        ...prev,
        loading: true,
        open: true,
      }));

      try {
        const res = await connectionRef.current({
          url: "/events/custom/with-active-announcements",
          method: "POST",
          body: {
            search: normalizedText,
          },
        });

        if (currentRequestId !== requestIdRef.current) return;

        if (res?.statusCode !== 200) {
          setUiState((prev) => ({
            ...prev,
            results: [],
            activeIndex: -1,
            loading: false,
          }));

          return;
        }

        const items = res?.data?.items ?? [];
        const mappedResults = mapEvents(items);

        cacheRef.current.set(normalizedText, mappedResults);

        setUiState((prev) => ({
          ...prev,
          results: mappedResults,
          activeIndex: mappedResults.length ? 0 : -1,
          loading: false,
        }));
      } catch (err) {
        if (currentRequestId !== requestIdRef.current) return;

        console.error("Error buscando eventos", err?.message);

        setUiState((prev) => ({
          ...prev,
          results: [],
          activeIndex: -1,
          loading: false,
        }));
      }
    },
    [mapEvents, resetResults]
  );

  useEffect(() => {
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }

    if (!canSearch) {
      resetResults();
      return;
    }

    const timeoutId = setTimeout(() => {
      searchEventsRequest(searchText);
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [searchText, canSearch, debounceMs, resetResults, searchEventsRequest]);

  useEffect(() => {
    function handleDocumentClick(e) {
      if (!boxRef.current) return;

      if (!boxRef.current.contains(e.target)) {
        setUiState((prev) => {
          if (!prev.open) return prev;

          return {
            ...prev,
            open: false,
          };
        });
      }
    }

    document.addEventListener("mousedown", handleDocumentClick);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, []);

  const handleChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearch(value);

      const shouldOpen = value.trim().length >= minLength;

      setUiState((prev) => {
        if (shouldOpen) {
          if (prev.open) return prev;

          return {
            ...prev,
            open: true,
          };
        }

        if (
          !prev.open &&
          !prev.loading &&
          prev.results.length === 0 &&
          prev.activeIndex === -1
        ) {
          return prev;
        }

        return {
          ...prev,
          open: false,
          loading: false,
          results: [],
          activeIndex: -1,
        };
      });
    },
    [minLength]
  );

  const handleFocus = useCallback(() => {
    if (!canSearch) return;

    setUiState((prev) => {
      if (prev.open) return prev;

      return {
        ...prev,
        open: true,
      };
    });
  }, [canSearch]);

  const handleActiveIndex = useCallback((index) => {
    setUiState((prev) => {
      if (prev.activeIndex === index) return prev;

      return {
        ...prev,
        activeIndex: index,
      };
    });
  }, []);

  const handleSelect = useCallback(
    (item) => {
      skipNextSearchRef.current = true;

      setSearch(item?.name || "");
      setUiState(INITIAL_UI_STATE);

      onSelect?.(item);
    },
    [onSelect]
  );

  return (
    <div
      className={wrapperClassName}
      ref={boxRef}
      role="combobox"
      aria-expanded={open}
      aria-haspopup="listbox"
      aria-owns="search-results"
    >
      <Input
        type="text"
        placeholder="Buscar espectáculos, intérpretes, tours"
        className={styles.searchInput}
        icon={inputIcon}
        style={inputStyle}
        value={search}
        onFocus={handleFocus}
        onChange={handleChange}
      />

      {open && loading && canSearch && (
        <div className={styles.noResults}>Buscando...</div>
      )}

      {open && !loading && results.length > 0 && (
        <ul
          id="search-results"
          className={styles.results}
          role="listbox"
          aria-label="Resultados de búsqueda"
        >
          {results.map((event, index) => (
            <SearchResultItem
              key={event.event_id || event.artist_id || `${event.name}-${index}`}
              item={event}
              index={index}
              isActive={index === activeIndex}
              onHover={handleActiveIndex}
              onSelect={handleSelect}
            />
          ))}
        </ul>
      )}

      {open && !loading && canSearch && results.length === 0 && (
        <div className={styles.noResults}>Sin resultados</div>
      )}
    </div>
  );
});

export default SearchEventsInput;